import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiWrite, list } from '../lib/api'
import { PageHead, Skel } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Shared rate limits (part 74). The counters live in Postgres, so the numbers below are the
 * combined usage of every API instance, not one process's view. The page is read-only apart
 * from reset: limits are deployment configuration, not something an operator edits at runtime.
 */
interface SharedLimitRow {
  bucket: string
  window_start: string
  window_seconds: number
  reset_at: string
  used: number
  limit: number
  remaining: number
}

interface ResetResult {
  bucket: string
  deleted: number
  reset_at: string
}

const LIMITS_KEY = ['shared-limits']

function windowLabel(seconds: number): string {
  if (seconds > 0 && seconds % 3600 === 0) return `${seconds / 3600} ชั่วโมง`
  if (seconds > 0 && seconds % 60 === 0) return `${seconds / 60} นาที`
  return `${seconds} วินาที`
}

function usageTone(percent: number): string {
  if (percent >= 100) return 'var(--color-crimson)'
  if (percent >= 80) return 'var(--color-amber)'
  return 'var(--color-gold)'
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

export function SharedLimits() {
  const qc = useQueryClient()
  const [confirmBucket, setConfirmBucket] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const limits = useQuery({
    queryKey: LIMITS_KEY,
    queryFn: () => list<SharedLimitRow>('/v1/shared-limits'),
    // Counters move with live traffic; a short poll keeps the operator's view honest without
    // a websocket or a manual refresh button.
    refetchInterval: 10_000,
    staleTime: 5_000,
  })

  const reset = useMutation({
    mutationFn: (bucket: string) =>
      apiWrite<ResetResult>(`/v1/shared-limits/${encodeURIComponent(bucket)}/reset`, 'POST'),
    onSuccess: (result) => {
      setNotice(`รีเซ็ต bucket ${result.bucket} แล้ว · ลบ ${number(result.deleted)} แถว`)
      void qc.invalidateQueries({ queryKey: LIMITS_KEY })
    },
    onSettled: () => setConfirmBucket(null),
  })

  const rows = limits.data ?? []
  const saturated = rows.filter((row) => row.limit > 0 && row.used >= row.limit).length
  const near = rows.filter((row) => row.limit > 0 && row.used < row.limit && row.used / row.limit >= 0.8).length

  return (
    <>
      <PageHead
        title="ขีดจำกัดการเรียกใช้ร่วมกัน"
        sub="ตัวนับกลางในฐานข้อมูล · ทุกอินสแตนซ์ของ API ใช้โควตาเดียวกัน ไม่คูณตามจำนวนเครื่อง"
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{rows.length} bucket</span>
        <span className="chip">{near} ใกล้เต็ม</span>
        <span className="chip" style={saturated > 0 ? { color: 'var(--color-crimson)' } : undefined}>
          {saturated} เต็มแล้ว
        </span>
        <span className="chip">รีเฟรชอัตโนมัติทุก 10 วินาที</span>
      </div>

      {notice ? (
        <div
          className="card card-pad"
          role="status"
          style={{ marginBottom: 16, borderColor: 'var(--color-hairline-strong)' }}
        >
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      <div className="card card-pad">
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="card-label">bucket ที่ประกาศไว้</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            ตัวเลขคือการใช้ร่วมกันของทุกอินสแตนซ์ในหน้าต่างปัจจุบัน
          </span>
        </div>

        {limits.isLoading ? <Skel h={220} /> : limits.isError ? (
          <div className="form-error">
            {errorText(limits.error, 'โหลดขีดจำกัดการเรียกใช้ไม่สำเร็จ')}
          </div>
        ) : rows.length === 0 ? (
          <div className="dim" style={{ fontSize: 12.5, lineHeight: 1.8 }}>
            ยังไม่มี bucket ที่ประกาศไว้ · เมื่อมีการเรียกใช้ครั้งแรก แถวจะปรากฏที่นี่พร้อมตัวนับของหน้าต่างนั้น
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table data">
              <thead>
                <tr>
                  <th>bucket</th>
                  <th style={{ width: 120 }}>หน้าต่าง</th>
                  <th style={{ width: 220 }}>ใช้ไป / เพดาน</th>
                  <th style={{ width: 100, textAlign: 'right' }}>เหลือ</th>
                  <th style={{ width: 150 }}>รีเซ็ตเมื่อ</th>
                  <th style={{ width: 120 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const percent = row.limit > 0 ? Math.min(100, Math.round((row.used / row.limit) * 100)) : 0
                  const tone = usageTone(percent)
                  return (
                    <tr key={row.bucket} style={{ cursor: 'default' }}>
                      <td className="mono" style={{ fontSize: 11.5 }}>{row.bucket}</td>
                      <td className="dim" style={{ fontSize: 11.5 }}>{windowLabel(row.window_seconds)}</td>
                      <td>
                        <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                          <span className="tnum" style={{ fontSize: 12 }}>
                            {number(row.used)} / {number(row.limit)}
                          </span>
                          <span className="dim tnum" style={{ marginLeft: 'auto', fontSize: 11 }}>
                            {percent}%
                          </span>
                        </div>
                        <div
                          className="bar"
                          style={{ height: 5, marginTop: 6 }}
                          role="meter"
                          aria-valuemin={0}
                          aria-valuemax={row.limit}
                          aria-valuenow={Math.min(row.used, row.limit)}
                          aria-label={`การใช้ bucket ${row.bucket}`}
                        >
                          <i style={{ width: `${percent}%`, background: tone }} />
                        </div>
                      </td>
                      <td className="tnum" style={{ textAlign: 'right', fontSize: 12 }}>{number(row.remaining)}</td>
                      <td className="dim" style={{ fontSize: 11 }}>{thaiDateTime(row.reset_at)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {confirmBucket === row.bucket ? (
                          <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              className="btn btn-sm"
                              disabled={reset.isPending}
                              style={{ color: 'var(--color-crimson)' }}
                              onClick={() => reset.mutate(row.bucket)}
                            >
                              {reset.isPending ? 'กำลังรีเซ็ต…' : 'ยืนยัน'}
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={() => setConfirmBucket(null)}>
                              ยกเลิก
                            </button>
                          </span>
                        ) : (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => {
                              setNotice(null)
                              reset.reset()
                              setConfirmBucket(row.bucket)
                            }}
                          >
                            รีเซ็ต
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {reset.isError ? (
          <div className="form-error" style={{ marginTop: 12 }}>
            {errorText(reset.error, 'รีเซ็ต bucket ไม่สำเร็จ')}
          </div>
        ) : null}
      </div>

      <div className="card card-pad" style={{ marginTop: 22 }}>
        <div className="card-label">วิธีที่ตัวนับนี้ทำงาน</div>
        <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
          <li>ตัวนับเก็บในตาราง platform.rate_counters แยกตามองค์กร bucket และหน้าต่างเวลา</li>
          <li>การเพิ่มตัวนับเป็นคำสั่งเดียวแบบ atomic ทำให้สองอินสแตนซ์ที่รับคำขอพร้อมกันเห็นโควตาเดียวกัน</li>
          <li>หน้าต่างเริ่มต้นถูกปัดลงเป็นจำนวนเต็มของขนาด window ตามเวลา UTC ทุกเครื่องจึงใช้ขอบเขตเดียวกัน</li>
          <li>คำขอที่ถูกปฏิเสธไม่เพิ่มตัวนับ และจะได้รับ Retry-After เป็นจำนวนวินาทีเต็ม</li>
          <li>การรีเซ็ตจะลบทุกหน้าต่างของ bucket นั้นทันทีและมีผลกับทุกอินสแตนซ์โดยไม่ต้องรีสตาร์ต</li>
          <li>เพดานและขนาดหน้าต่างเป็นค่าตั้งต้นจากการ deploy ไม่ใช่ค่าที่แก้ได้จากหน้านี้</li>
        </ul>
      </div>
    </>
  )
}
